/**
 * Service de cache pour les assets 3D
 * Interface entre l'application et l'AssetCacheWorker
 * Gère le chargement des assets avec cache transparent
 */

import {
  AssetVersionDescriptor,
  getAssetUrl,
  getAssetVersionToken,
} from "@/lib/api/asset";
import { normalizeAssetMimeType } from "./assetMimeType";

// Types
interface AssetMetadata {
  id: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  cachedAt: number;
  lastAccessed: number;
  accessCount: number;
}

interface CacheStats {
  totalEntries: number;
  totalSize: number;
  maxSize: number;
  storageQuota?: number;
  storageUsage?: number;
  maxSizeSource?: "estimate" | "fallback";
}

interface CacheResponse {
  id: string;
  success: boolean;
  type: string;
  data?: ArrayBuffer;
  metadata?: AssetMetadata;
  exists?: boolean;
  list?: AssetMetadata[];
  error?: string;
  stats?: CacheStats;
}

type PendingPromise = {
  resolve: (value: unknown) => void;
  reject: (reason?: Error) => void;
};

interface AssetCacheLoadOptions extends AssetVersionDescriptor {}

interface AssetDescriptor extends AssetVersionDescriptor {
  id: string;
  fileName: string;
  mimeType: string;
}

/**
 * Service singleton pour gérer le cache des assets 3D
 */
class AssetCacheService {
  private worker: Worker | null = null;
  private messageId = 0;
  private pendingPromises = new Map<string, PendingPromise>();
  private isInitialized = false;

  /**
   * Initialise le worker de cache
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Créer le worker avec un import dynamique
      const WorkerConstructor = await import("./AssetCacheWorker?worker");
      this.worker = new WorkerConstructor.default();

      this.worker.onmessage = (event: MessageEvent<CacheResponse>) => {
        this.handleWorkerMessage(event.data);
      };

      this.worker.onerror = (error) => {
        console.error("[AssetCacheService] Worker error:", error);
        this.rejectAllPending(new Error("Worker error"));
      };

      this.isInitialized = true;
      console.log("[AssetCacheService] Initialisé");
    } catch (error) {
      console.error("[AssetCacheService] Échec de l'initialisation:", error);
      throw error;
    }
  }

  /**
   * Arrête le worker et nettoie les ressources
   */
  terminate(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.rejectAllPending(new Error("Service terminated"));
    this.isInitialized = false;
  }

  /**
   * Charge un asset avec cache transparent
   * Retourne une URL blob si l'asset est en cache, ou l'URL réseau sinon
   */
  async loadAsset(
    assetId: string,
    fileName: string,
    mimeType: string,
    options: AssetCacheLoadOptions = {},
  ): Promise<string> {
    await this.ensureInitialized();
    const cacheKey = this.getCacheKey(assetId, options);
    const version = getAssetVersionToken(options);

    const normalizedMimeType = normalizeAssetMimeType(fileName, mimeType);

    // Vérifier si l'asset est en cache
    const cached = await this.retrieveFromCache(cacheKey);

    if (cached) {
      console.log(
        `[AssetCacheService] Cache hit pour asset ${cacheKey} (${cached.data.byteLength} bytes)`,
      );
      // Vérifier que les données ne sont pas vides
      if (cached.data.byteLength === 0) {
        console.warn(
          `[AssetCacheService] Données en cache vides pour ${assetId}, suppression et re-téléchargement`,
        );
        await this.removeFromCache(assetId, options);
      } else {
        // Créer un blob URL à partir des données en cache
        const blob = new Blob([cached.data], { type: normalizedMimeType });
        return URL.createObjectURL(blob);
      }
    }

    console.log(
      `[AssetCacheService] Cache miss pour asset ${cacheKey}, téléchargement...`,
    );

    // Télécharger l'asset
    const url = getAssetUrl(assetId, { version });
    const response = await fetch(url, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(
        `Échec du téléchargement: ${response.status} ${response.statusText}`,
      );
    }

    const arrayBuffer = await response.arrayBuffer();
    const fileSize = arrayBuffer.byteLength;

    // Vérifier que les données téléchargées ne sont pas vides
    if (fileSize === 0) {
      throw new Error(`Fichier téléchargé vide: ${fileName}`);
    }

    console.log(
      `[AssetCacheService] Téléchargé ${fileSize} bytes pour ${assetId}`,
    );

    // IMPORTANT: Créer le blob URL AVANT de stocker dans le cache
    // car le stockage transfère l'ArrayBuffer au worker (detached)
    const blob = new Blob([arrayBuffer], { type: normalizedMimeType });
    const blobUrl = URL.createObjectURL(blob);

    // Stocker dans le cache (fire and forget, ne pas bloquer le rendu)
    // On crée une copie de l'ArrayBuffer pour le cache car l'original est utilisé par le blob
    const cacheBuffer = arrayBuffer.slice(0);
    this.storeInCache(
      assetId,
      {
        fileName,
        mimeType: normalizedMimeType,
        fileSize,
      },
      cacheBuffer,
      cacheKey,
    ).catch((err) => {
      console.warn("[AssetCacheService] Échec du stockage en cache:", err);
    });

    return blobUrl;
  }

  /**
   * Charge un asset avec gestion de la mémoire (libère le blob après usage)
   * Utiliser cette méthode quand vous avez besoin des données brutes
   */
  async loadAssetData(
    assetId: string,
    fileName: string,
    mimeType: string,
    options: AssetCacheLoadOptions = {},
  ): Promise<ArrayBuffer> {
    await this.ensureInitialized();
    const cacheKey = this.getCacheKey(assetId, options);
    const version = getAssetVersionToken(options);

    // Vérifier le cache d'abord
    const cached = await this.retrieveFromCache(cacheKey);
    if (cached) {
      console.log(
        `[AssetCacheService] Cache hit (data) pour asset ${cacheKey}`,
      );
      return cached.data;
    }

    // Télécharger
    const url = getAssetUrl(assetId, { version });
    const response = await fetch(url, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(
        `Échec du téléchargement: ${response.status} ${response.statusText}`,
      );
    }

    const arrayBuffer = await response.arrayBuffer();
    const fileSize = arrayBuffer.byteLength;

    // Vérifier que les données téléchargées ne sont pas vides
    if (fileSize === 0) {
      throw new Error(`Fichier téléchargé vide: ${fileName}`);
    }

    // Stocker en cache
    this.storeInCache(
      assetId,
      {
        fileName,
        mimeType,
        fileSize,
      },
      arrayBuffer,
      cacheKey,
    ).catch((err) => {
      console.warn("[AssetCacheService] Échec du stockage en cache:", err);
    });

    return arrayBuffer;
  }

  /**
   * Vérifie si un asset est présent dans le cache
   */
  async isCached(
    assetId: string,
    options: AssetCacheLoadOptions = {},
  ): Promise<boolean> {
    await this.ensureInitialized();
    return this.sendMessage<boolean>("exists", {
      assetId: this.getCacheKey(assetId, options),
    });
  }

  /**
   * Supprime un asset du cache
   */
  async removeFromCache(
    assetId: string,
    options: AssetCacheLoadOptions = {},
  ): Promise<void> {
    await this.ensureInitialized();
    await this.sendMessage("delete", {
      assetId: this.getCacheKey(assetId, options),
    });
  }

  /**
   * Vide complètement le cache
   */
  async clearCache(): Promise<void> {
    await this.ensureInitialized();
    await this.sendMessage("clear");
  }

  /**
   * Liste tous les assets en cache
   */
  async listCachedAssets(): Promise<AssetMetadata[]> {
    await this.ensureInitialized();
    const response = await this.sendMessage<CacheResponse>("list");
    return response.list ?? [];
  }

  /**
   * Récupère les statistiques du cache
   */
  async getStats(): Promise<CacheStats> {
    await this.ensureInitialized();
    const response = await this.sendMessage<CacheResponse>("list");
    if (!response.stats) {
      // Fallback si le worker ne retourne pas les stats
      const list = response.list ?? [];
      const totalSize = list.reduce(
        (sum, meta) => sum + (meta.fileSize || 0),
        0,
      );
      return {
        totalEntries: list.length,
        totalSize,
        maxSize: 512 * 1024 * 1024,
        maxSizeSource: "fallback",
      };
    }
    return response.stats;
  }

  /**
   * Précharge un ensemble d'assets dans le cache
   * Utile pour précharger les modèles avant navigation
   */
  async preloadAssets(
    assets: AssetDescriptor[],
  ): Promise<void> {
    await this.ensureInitialized();

    const promises = assets.map(async (asset) => {
      const versionDescriptor = this.toVersionDescriptor(asset);
      const isCached = await this.isCached(asset.id, versionDescriptor);
      if (!isCached) {
        try {
          await this.loadAssetData(
            asset.id,
            asset.fileName,
            asset.mimeType,
            versionDescriptor,
          );
          console.log(`[AssetCacheService] Préchargement terminé: ${asset.id}`);
        } catch (error) {
          console.warn(
            `[AssetCacheService] Échec du préchargement ${asset.id}:`,
            error,
          );
        }
      }
    });

    await Promise.all(promises);
  }

  // Méthodes privées

  private async ensureInitialized(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }
  }

  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem("token");
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  }

  private async storeInCache(
    assetId: string,
    metadata: Omit<
      AssetMetadata,
      "id" | "cachedAt" | "lastAccessed" | "accessCount"
    >,
    data: ArrayBuffer,
    cacheKey = assetId,
  ): Promise<void> {
    await this.sendMessage(
      "store",
      {
        assetId: cacheKey,
        metadata,
        data,
      },
      [data],
    );
  }

  private async retrieveFromCache(
    assetId: string,
  ): Promise<{ data: ArrayBuffer; metadata: AssetMetadata } | null> {
    const response = await this.sendMessage<CacheResponse>("retrieve", {
      assetId,
    });
    if (response.data && response.metadata) {
      return {
        data: response.data,
        metadata: response.metadata,
      };
    }
    return null;
  }

  private getCacheKey(
    assetId: string,
    options: AssetCacheLoadOptions = {},
  ): string {
    const version = getAssetVersionToken(options);
    return version ? `${assetId}:${version}` : assetId;
  }

  private toVersionDescriptor(asset: AssetDescriptor): AssetVersionDescriptor {
    return {
      fileSize: asset.fileSize,
      createdAt: asset.createdAt,
      storagePath: asset.storagePath,
    };
  }

  private sendMessage<T>(
    type: string,
    payload: Record<string, unknown> = {},
    transfer: Transferable[] = [],
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      if (!this.worker) {
        reject(new Error("Worker non initialisé"));
        return;
      }

      const id = `${type}_${++this.messageId}_${Date.now()}`;
      this.pendingPromises.set(id, {
        resolve: resolve as (value: unknown) => void,
        reject,
      });

      const message = {
        id,
        type,
        ...payload,
      };

      if (transfer.length > 0) {
        this.worker.postMessage(message, transfer);
      } else {
        this.worker.postMessage(message);
      }
    });
  }

  private handleWorkerMessage(response: CacheResponse): void {
    const pending = this.pendingPromises.get(response.id);
    if (!pending) {
      console.warn(`[AssetCacheService] Message inattendu: ${response.id}`);
      return;
    }

    this.pendingPromises.delete(response.id);

    if (response.success) {
      // Toujours résoudre avec la réponse complète pour que les appelants
      // puissent accéder à toutes les propriétés (list, stats, data, etc.)
      switch (response.type) {
        case "exists":
          pending.resolve(response.exists as unknown);
          break;
        case "retrieve":
        case "list":
          pending.resolve(response as unknown);
          break;
        case "store":
        case "delete":
        case "clear":
          pending.resolve(response as unknown);
          break;
        default:
          pending.resolve(response as unknown);
      }
    } else {
      pending.reject(new Error(response.error || "Erreur worker inconnue"));
    }
  }

  private rejectAllPending(error: Error): void {
    for (const [, pending] of this.pendingPromises) {
      pending.reject(error);
    }
    this.pendingPromises.clear();
  }
}

// Export du singleton
export const assetCacheService = new AssetCacheService();

// Alias de compatibilité (ancien nom orienté LOD)
export const lodCacheService = assetCacheService;

// Types exportés
export type { AssetMetadata, CacheStats };
